import { Injectable, Signal, computed, inject } from '@angular/core';
import { IFeatureConfig } from '@partners/api';
import { CatalogService, TCatalogOption } from '@partners/catalog';
import { HttpService } from '@partners/http';
import { TFeature } from '@partners/settings';
import { TConfigPartition, TLink } from '@partners/types';
import { coerceDictionary } from '@partners/utils';
import { NavigationAdapter } from '../../navigation';
import { PolicyService } from '../../process';

/**
 * @description
 * this is the base class of a domain's business logic service. all features
 * which inherits this base class will gain:
 * 1. generic load & send methods interfaces
 * 2. access to the feature's state data (- see StateAccessService)
 * 3. >> TBD: access to the feature's static config data <<
 */

@Injectable({
  providedIn: 'root'
})
export abstract class FeatureService<T> /*extends StateAccessService<T>*/ {
  /**
   * each inheritor is responsible to override this property and init it's own config map
   * (see findConfig$$ method down below which each inheritor should call in constructor)
   */
  abstract readonly featureConfig$$: Signal<IFeatureConfig>;

  /** accessor to config text dictionary */
  readonly configText$$ = computed<TConfigPartition<string>>(() => this.featureConfig$$()?.text);

  /** accessor to config boolean indications dictionary */
  readonly configIndication$$ = computed<TConfigPartition<boolean>>(() => this.featureConfig$$()?.indication);

  /** accessor to config links dictionary */
  readonly configLink$$ = computed<TConfigPartition<TLink>>(() => this.featureConfig$$()?.links);

  /** accessor to config params dictionary */
  readonly configParams$$ = computed<TConfigPartition<any>>(() => this.featureConfig$$()?.params);

  protected _httpService = inject(HttpService);
  protected _policyService = inject(PolicyService);
  protected _catalogService = inject(CatalogService);
  protected _navAdapter = inject(NavigationAdapter);

  constructor() {
    // super();
  }

  protected fromFinanceCatalog<T = TCatalogOption>(key: string): T[] {
    return this._catalogService.fromFinance<T>(key);
  }

  protected fromMetadataCatalog<T = TCatalogOption>(key: string): T[] {
    return this._catalogService.fromMetadata<T>(key);
  }

  /**
   * @description
   * read a specific value from the feature's config text dictionary
   *
   * @param key the name of the dictionary record to read
   * @returns a string corresponding to key
   */
  fromText$$(key: string): string {
    const textDictionary = coerceDictionary<TConfigPartition<string>>(this.configText$$());
    return textDictionary[key];
  }
  fromLink$$(key: string): TLink {
    const linkDictionary = coerceDictionary<TConfigPartition<TLink>>(this.configLink$$());
    return linkDictionary[key];
  }
  fromParams$$<T>(key: string): T {
    const paramDictionary = coerceDictionary<TConfigPartition<T>>(this.configParams$$());
    return paramDictionary[key];
  }
  fromIndication$$(key: string): boolean {
    const indicationDictionary = coerceDictionary<TConfigPartition<boolean>>(this.configIndication$$());
    return indicationDictionary[key];
  }
  /**
   * @description
   * this is an initialization method. every inheritor of this class which is required to
   * read its policy's config node must call this method to initialize the feature's config
   * data for reading texts | indications | links etc' on demand
   *
   * @param featureName the name of the feature (domain) which config node we want to read
   * @returns a signal of feature config node (texts | indications | links)
   */
  protected findConfig$$(featureName: TFeature): Signal<IFeatureConfig> {
    return this._policyService.findFeatureConfig$$(featureName);
  }
}
